<script lang="ts">
    import { toast } from 'svelte-sonner';
    import { useForm, page } from '@inertiajs/svelte';
    import Modal from '@/components/Modal.svelte';
    import FormContainer from '@/components/Form/FormContainer.svelte';
    import FormBlock from '@/components/Form/FormBlock.svelte';
    import { Button } from '@/components/ui/button';
    import Input from '@/components/Form/Inputs/Input.svelte';
    import Textarea from '@/components/Inputs/Textarea.svelte';
    import DateTime from '@/components/Inputs/DateTime.svelte';
    import type Blocks from '@/schemas/public/Blocks';
    import type { Field } from '@/types/types';
    import { router } from '@inertiajs/svelte';
    import FilePond, { registerPlugin } from 'svelte-filepond';
    import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
    import 'filepond/dist/filepond.min.css';
    import { uploadImages } from '@/lib/imageUpload';

    registerPlugin(FilePondPluginFileValidateSize);

    type Props = {
        blocks: (Blocks & { fields: Field[] })[];
        show: boolean;
        employeeId: string;
        onSuccess?: () => void;
    };

    let { blocks, show = $bindable(), employeeId, onSuccess }: Props = $props();

    let pondFiles = $state<any[]>([]);
    let pendingImageFiles = $state<File[]>([]);

    function handleFilePondAddFile(err: any, fileItem: any) {
        if (!err) {
            pendingImageFiles = [...pendingImageFiles, fileItem.file];
        }
    }

    function handleFilePondRemoveFile(err: any, fileItem: any) {
        if (!err) {
            pendingImageFiles = pendingImageFiles.filter(f => f !== fileItem.file);
        }
    }

    function close() {
        show = false;
        pondFiles = [];
        pendingImageFiles = [];
    }

    // Initialize form from blocks
    let createFormInner: Record<string, any> = {};
    for (const block of blocks) {
        for (const field of block.fields) {
            createFormInner[field.column_name] = null;
        }
    }

    let createForm = $state(useForm(createFormInner));

    function submit(e: Event) {
        e.preventDefault();
        $createForm.processing = true;

        router.post(route('harnesses.store', { employee: employeeId }), $createForm.data(), {
            onSuccess: async (pageData: any) => {
                const harnessId = pageData.props?.flash?.harnessId;

                if (pendingImageFiles.length > 0 && harnessId) {
                    try {
                        const csrfToken = $page.props.csrfToken || '';
                        await uploadImages(
                            pendingImageFiles,
                            'App\\Models\\Harness',
                            harnessId,
                            csrfToken
                        );
                    } catch (error) {
                        console.error('Error uploading images:', error);
                        toast.error('Harness created but some images failed to upload');
                    }
                }

                close();
                $createForm.reset();
                toast.success('Harness created successfully!');
                onSuccess?.();
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error(`Error creating harness: ${message as string}`);
                }
                $createForm.processing = false;
            },
            onFinish: () => {
                $createForm.processing = false;
            },
        });
    }
</script>

{#if show}
    <Modal title="Add New Harness" onClose={close}>
        <FormContainer>
            <form onsubmit={submit}>
                {#each blocks as block (block.id)}
                    <FormBlock label={block.label}>
                        {#each block.fields as field (field.id)}
                            {#if field.editable !== false}
                                {#if field.ui_type === 'textarea'}
                                    <Textarea
                                        label={field.label}
                                        bind:value={$createForm[field.column_name]}
                                        error={$createForm.errors[field.column_name]}
                                        {field}
                                    />
                                {:else if field.ui_type === 'datetime'}
                                    <DateTime
                                        label={field.label}
                                        bind:value={$createForm[field.column_name]}
                                        error={$createForm.errors[field.column_name]}
                                        {field}
                                    />
                                {:else}
                                    <Input
                                        {field}
                                        bind:value={$createForm[field.column_name]}
                                        error={$createForm.errors[field.column_name]}
                                        onkeydown={() => {}}
                                    />
                                {/if}
                            {/if}
                        {/each}
                    </FormBlock>
                {/each}

                <FormBlock label="Images">
                    <div class="col-span-full">
                        <p class="text-sm text-muted-foreground mb-2">
                            Upload photos of the harness
                        </p>
                        <FilePond
                            bind:files={pondFiles}
                            server={null}
                            allowMultiple={true}
                            maxFiles={10}
                            maxFileSize="30MB"
                            labelMaxFileSizeExceeded="File is too large"
                            labelMaxFileSize={"Maximum file size is {filesize}"}
                            onwarning={(error) => {
                                if (error.body) toast.error(error.body);
                            }}
                            name="images"
                            acceptedFileTypes={['image/*']}
                            labelIdle='Drag & Drop your images or <span class="filepond--label-action">Browse</span>'
                            onaddfile={handleFilePondAddFile}
                            onremovefile={handleFilePondRemoveFile}
                        />
                    </div>
                </FormBlock>

                <div class="mt-5 flex justify-end gap-2">
                    <Button type="button" onclick={close}>Cancel</Button>
                    <Button type="submit" disabled={$createForm.processing}>
                        {#if $createForm.processing}Saving...{:else}Save{/if}
                    </Button>
                </div>
            </form>
        </FormContainer>
    </Modal>
{/if}